import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, AsOfBar } from "@/components/PageHeader";
import { ConfidenceBadge, RegistryBadge } from "@/components/Badges";
import { Search, MapPin, ArrowRight } from "lucide-react";

const EXAMPLES = ["AAICM0358E", "DL/2017/0152816", "231662040", "CSR00000373"];

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/v1/ngo/suggest", { params: { q, state } });
        setSuggestions(res.data.suggestions);
        setShowSug(true);
      } catch (e) { console.debug("suggest fetch failed", e); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, state]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selectSuggestion = (s) => {
    setShowSug(false);
    navigate(`/app/ngo/${encodeURIComponent(s.entity_key)}`);
  };

  const run = async () => {
    setShowSug(false);
    setLoading(true);
    try {
      const res = await api.get("/v1/ngo/search", { params: { q, state } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Universal crosswalk search"
        title="Find an NGO"
        subtitle="Search by name, PAN, NGO Darpan ID, FCRA number or CSR-1 — the closest matches appear as you type."
      />

      <div className="flex flex-col sm:flex-row gap-3 max-w-3xl mb-6">
        <div className="relative flex-1" ref={boxRef}>
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="ngo-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSug(true)}
            onKeyDown={(e) => e.key === "Enter" && (suggestions.length > 0 ? selectSuggestion(suggestions[0]) : run())}
            placeholder="Search by name, PAN, Darpan ID, FCRA no. or CSR-1…"
            autoComplete="off"
            className="w-full rounded-full border border-slate-700 bg-slate-950/70 pl-10 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors duration-150"
          />
          {showSug && suggestions.length > 0 && (
            <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-xl animate-fade-up">
              {suggestions.map((s) => (
                <button
                  key={s.entity_key}
                  data-testid={`suggestion-${s.entity_key}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectSuggestion(s)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-slate-800/70 transition-colors duration-100 border-b border-slate-800 last:border-0"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-100">{s.name}</span>
                    <span className="font-mono text-[11px] text-slate-500">{s.matched ? `${s.matched.type} · ${s.matched.value}` : s.pan}{s.state ? ` · ${s.state}` : ""}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative sm:w-56">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="search-registry-filter"
            value={state}
            onChange={(e) => setState(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="State (optional)"
            className="w-full rounded-full border border-slate-700 bg-slate-950/70 pl-10 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors duration-150"
          />
        </div>
        <button
          onClick={run}
          data-testid="ngo-search-button"
          className="rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-blue-400 transition-colors duration-150"
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] text-slate-500">Try:</span>
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            data-testid={`search-example-${ex}`}
            onClick={() => { setQ(ex); run(); }}
            className="rounded-full border border-slate-700 px-3 py-1 font-mono text-[11px] text-slate-300 hover:border-blue-500 hover:text-blue-300 transition-colors duration-150"
          >
            {ex}
          </button>
        ))}
      </div>

      {data && (
        <>
          <div className="mb-4 font-mono text-xs text-slate-500">{data.count} result(s)</div>
          <div className="grid gap-3 lg:grid-cols-2">
            {data.results.map((r) => (
              <button
                key={r.entity_key}
                data-testid={`search-result-${r.entity_key}`}
                onClick={() => navigate(`/app/ngo/${encodeURIComponent(r.entity_key)}`)}
                className="group text-left rounded-xl border border-slate-800 bg-slate-900/60 p-4 hover:border-blue-700/60 hover:bg-slate-900 transition-colors duration-150"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{r.name}</div>
                    <div className="font-mono text-[11px] text-slate-500 mt-0.5">{r.pan} · {r.state}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 transition-colors duration-150 shrink-0" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {r.pan && <RegistryBadge registry="incometax" />}
                  {r.darpan_ids?.length > 0 && <RegistryBadge registry="darpan" />}
                  {r.fcra_reg_no && <RegistryBadge registry="fcra" />}
                  {r.csr1 && <RegistryBadge registry="csr1" />}
                  {r.overall_tier && <ConfidenceBadge info={r.overall_tier} className="ml-auto" />}
                </div>
              </button>
            ))}
          </div>
          <div className="mt-6 max-w-3xl"><AsOfBar asOf={data.as_of} /></div>
        </>
      )}
    </div>
  );
}
